import { useCallback, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from '../components/Toast';

const FUNCTIONS_BASE = 'https://us-central1-sistema-bks.cloudfunctions.net';

/**
 * Pro->Starter downgrade flow (eligibility check + preview + immediate
 * settle + Firestore sync wait). Mirrors useUpgradeToPro.js's shape, but a
 * downgrade can come back BLOCKED (usage/addons exceed Starter's limits) —
 * that's surfaced as its own state, not a generic error toast, so the modal
 * can render the specific list of what needs to change.
 *
 * @param {{mode: string, orgId?: string}} context - from useOrganization()
 */
// onSynced (opcional): si se pasa, se llama en vez de window.location.reload()
// cuando el webhook confirma el cambio (mismo patrón que useUpgradeToPro).
export default function useDowngradeToStarter(context, { onSynced } = {}) {
  const [prorrateo, setProrrateo] = useState(null); // { monto, moneda, prorationDate }
  const [blocked, setBlocked] = useState(null); // { usageViolations, addonViolations }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { t } = useTranslation('addons');
  const auth = getAuth();
  const db = getFirestore();

  const orgPayload = useCallback(() => (
    context.mode === 'organization' && context.orgId ? { organizationId: context.orgId } : {}
  ), [context.mode, context.orgId]);

  const calcularPreview = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return null;

    setLoadingPreview(true);
    setBlocked(null);
    try {
      const idToken = await user.getIdToken();
      const res = await axios.post(
        `${FUNCTIONS_BASE}/calcularProrrateoDowngrade`,
        orgPayload(),
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (res.data.success) {
        const data = {
          monto: res.data.monto,
          moneda: res.data.currency.toUpperCase(),
          prorationDate: res.data.prorationDate,
        };
        setProrrateo(data);
        return data;
      }
      return null;
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.code === 'DOWNGRADE_BLOCKED') {
        setBlocked({
          usageViolations: error.response.data.usageViolations || [],
          addonViolations: error.response.data.addonViolations || [],
        });
      } else {
        console.error('❌ Error calculando prorrateo de downgrade:', error);
        toast.error(error.response?.data?.error || t('paymentError'));
      }
      setProrrateo(null);
      return null;
    } finally {
      setLoadingPreview(false);
    }
  }, [auth, orgPayload, t]);

  // Mismo mecanismo que useUpgradeToPro.js: espera a que el webhook
  // (customer.subscription.updated) confirme el cambio en Firestore en vez
  // de recargar a ciegas.
  const waitForPlanSync = useCallback((targetPlan) => {
    return new Promise((resolve) => {
      const user = auth.currentUser;
      const docRef = context.mode === 'organization' && context.orgId
        ? doc(db, 'organizations', context.orgId)
        : doc(db, 'clientes', user.uid);

      let settled = false;

      const finish = (synced) => {
        if (settled) return;
        settled = true;
        unsubscribe();
        clearTimeout(timeoutId);
        toast[synced ? 'success' : 'info'](t(synced ? 'upgradeSynced' : 'upgradeSyncDelayed'));
        if (onSynced) onSynced(); else window.location.reload();
        resolve();
      };

      const unsubscribe = onSnapshot(docRef, (snap) => {
        const data = snap.data();
        const rawPlan = data?.planId || data?.plan;
        const currentPlan = (rawPlan?.id || rawPlan || '').toString().toLowerCase();
        if (currentPlan === targetPlan) finish(true);
      }, () => finish(false));

      const timeoutId = setTimeout(() => finish(false), 7000);
    });
  }, [auth, context.mode, context.orgId, db, t, onSynced]);

  const confirmarDowngrade = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(t('loginRequired'));
      return;
    }

    setProcessing(true);
    try {
      const idToken = await user.getIdToken();
      const payload = { ...orgPayload() };
      if (prorrateo?.prorationDate) {
        payload.prorationDate = prorrateo.prorationDate;
      }

      const res = await axios.post(
        `${FUNCTIONS_BASE}/downgradeToStarter`,
        payload,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const monto = res.data?.prorrateo?.monto ?? prorrateo?.monto;
      const moneda = res.data?.prorrateo?.moneda || prorrateo?.moneda;
      toast.success(t('upgradeComplete', { amount: monto ?? 'N/A', currency: moneda || '' }));

      await waitForPlanSync('starter');
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.code === 'DOWNGRADE_BLOCKED') {
        // El estado cambió entre el preview y la confirmación (ej. se agregó
        // un bot en otra pestaña) — se re-valida server-side y se muestra la
        // lista actualizada en vez de un error genérico.
        setBlocked({
          usageViolations: error.response.data.usageViolations || [],
          addonViolations: error.response.data.addonViolations || [],
        });
      } else {
        console.error('❌ Error al hacer downgrade a Starter:', error);
        toast.error(error.response?.data?.error || t('paymentError'));
      }
    } finally {
      setProcessing(false);
    }
  }, [auth, orgPayload, prorrateo, waitForPlanSync, t]);

  return { prorrateo, blocked, loadingPreview, processing, calcularPreview, confirmarDowngrade };
}
